/**
 * OOXML track-change segment helpers for clause-card edit mode (logging removed).
 */

import { TrackChangesParser } from '../word/utils/TrackChangesParser';
import { textSnapshot, type TextSnapshot } from './suggestEditDebug';

export type WordEditCaptureSource = 'word-paragraph' | 'clause-card-edit';

export interface TrackChangeSegmentSummary {
  index: number;
  kind: 'ins' | 'del' | 'plain';
  author: string | null;
  textPreview: string;
  textLen: number;
  isOurSide: boolean;
}

export interface ParagraphEditSnapshot {
  source: WordEditCaptureSource;
  seq: number;
  timestamp: string;
  acceptedText: string;
  originalText: string;
  acceptedSnapshot: TextSnapshot;
  originalSnapshot: TextSnapshot;
  ooxmlLen: number;
  hasIns: boolean;
  hasDel: boolean;
  trackChangeSegments: TrackChangeSegmentSummary[];
  selectionText?: string;
  selectionLen?: number;
  paragraphIndex?: number;
  changeTrackingMode?: string;
  /** contentEditable HTML summary (clause-card edit only). */
  domSummary?: Record<string, unknown>;
}

function isOurSideAuthor(author: string): boolean {
  const a = author.trim();
  return a === 'AI Suggestion' || a === 'User Edit';
}

function getOoxmlAuthor(el: Element): string {
  return (
    el.getAttribute('w:author') ||
    el.getAttributeNS('http://schemas.openxmlformats.org/wordprocessingml/2006/main', 'author') ||
    el.getAttribute('author') ||
    ''
  );
}

function getLocalName(el: Element): string {
  return el.localName || el.tagName.split(':').pop() || el.tagName;
}

/** Accepted text inside w:ins (w:t only; skips nested w:del). */
function extractInsAcceptedText(el: Element): string {
  let text = '';
  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      text += node.textContent || '';
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const elem = node as Element;
    const ln = getLocalName(elem);
    if (ln === 'del') return;
    if (ln === 't') {
      text += elem.textContent || '';
      return;
    }
    if (ln === 'delText') return;
    for (const child of Array.from(elem.childNodes)) walk(child);
  };
  walk(el);
  return text;
}

/** Deleted text inside w:del (w:delText nodes). */
function extractDelText(el: Element): string {
  let text = '';
  const walk = (node: Node) => {
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const elem = node as Element;
    const ln = getLocalName(elem);
    if (ln === 'delText' || ln === 't') {
      text += elem.textContent || '';
      return;
    }
    for (const child of Array.from(elem.childNodes)) walk(child);
  };
  walk(el);
  return text;
}

function extractRunText(el: Element): string {
  let text = '';
  for (const child of Array.from(el.childNodes)) {
    if (child.nodeType !== Node.ELEMENT_NODE) continue;
    const ln = getLocalName(child as Element);
    if (ln === 't') text += (child as Element).textContent || '';
  }
  return text;
}

/**
 * Document-order w:ins / w:del / plain-run inventory from paragraph OOXML.
 * One segment per markup block (no duplicate plain runs inside w:ins).
 */
export function summarizeOoxmlTrackChanges(ooxml: string): TrackChangeSegmentSummary[] {
  if (!ooxml?.trim()) return [];
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(ooxml, 'text/xml');
    if (doc.querySelector('parsererror')) return [];

    const para =
      doc.querySelector('w\\:p') ||
      doc.querySelector('p') ||
      doc.documentElement;
    if (!para) return [];

    const segments: TrackChangeSegmentSummary[] = [];
    let index = 0;

    const push = (kind: TrackChangeSegmentSummary['kind'], author: string | null, text: string) => {
      if (kind !== 'del' && !text) return;
      segments.push({
        index: index++,
        kind,
        author,
        textPreview: text.slice(0, 120),
        textLen: text.length,
        isOurSide: isOurSideAuthor(author ?? ''),
      });
    };

    /** Walk children; only emit top-level runs as plain (runs inside w:ins are part of that ins). */
    const visit = (parent: Element, emitPlainRuns: boolean) => {
      for (const child of Array.from(parent.childNodes)) {
        if (child.nodeType !== Node.ELEMENT_NODE) continue;
        const el = child as Element;
        const ln = getLocalName(el);
        if (ln === 'pPr' || ln === 'rPr' || ln === 'instrtext' || ln === 'fldchar') continue;

        if (ln === 'ins') {
          const author = getOoxmlAuthor(el);
          push('ins', author || null, extractInsAcceptedText(el));
          visit(el, false);
          continue;
        }

        if (ln === 'del') {
          const author = getOoxmlAuthor(el);
          push('del', author || null, extractDelText(el));
          continue;
        }

        if (ln === 'r' && emitPlainRuns) {
          push('plain', null, extractRunText(el));
          continue;
        }

        visit(el, emitPlainRuns);
      }
    };

    visit(para as Element, true);
    return segments;
  } catch {
    return [];
  }
}

/** Infer Word edit semantics from consecutive segment snapshots (for log annotation). */
export function inferWordEditSemantics(
  before: TrackChangeSegmentSummary[],
  after: TrackChangeSegmentSummary[],
): string[] {
  const notes: string[] = [];
  const beforeIns = before.filter((s) => s.kind === 'ins').map((s) => s.textPreview).join('|');
  const afterIns = after.filter((s) => s.kind === 'ins').map((s) => s.textPreview).join('|');
  const beforeDel = before.filter((s) => s.kind === 'del').map((s) => s.textPreview).join('|');
  const afterDel = after.filter((s) => s.kind === 'del').map((s) => s.textPreview).join('|');

  if (beforeDel !== afterDel) {
    notes.push('counterparty-or-original text marked deleted (w:del)');
  }
  const ownInsShrunk = beforeIns.length > afterIns.length && beforeDel === afterDel;
  if (ownInsShrunk) {
    notes.push('user pending insertion shrunk/removed without new w:del (silent own-ins edit)');
  }
  if (after.length < before.length && afterDel === beforeDel) {
    notes.push('OOXML blocks merged or own insertion collapsed');
  }
  return notes;
}

export function buildParagraphEditSnapshot(params: {
  source: WordEditCaptureSource;
  seq: number;
  ooxml?: string;
  acceptedText?: string;
  originalText?: string;
  selectionText?: string;
  paragraphIndex?: number;
  changeTrackingMode?: string;
  domSummary?: Record<string, unknown>;
}): ParagraphEditSnapshot {
  const ooxml = params.ooxml ?? '';
  const acceptedText =
    params.acceptedText ??
    (ooxml ? TrackChangesParser.extractAcceptedText(ooxml) : '');
  const originalText =
    params.originalText ??
    (ooxml ? TrackChangesParser.extractOriginalText(ooxml) : '');

  return {
    source: params.source,
    seq: params.seq,
    timestamp: new Date().toISOString(),
    acceptedText,
    originalText,
    acceptedSnapshot: textSnapshot(acceptedText),
    originalSnapshot: textSnapshot(originalText),
    ooxmlLen: ooxml.length,
    hasIns: ooxml.includes('<w:ins') || ooxml.includes(':ins'),
    hasDel: ooxml.includes('<w:del') || ooxml.includes(':del'),
    trackChangeSegments: summarizeOoxmlTrackChanges(ooxml),
    selectionText: params.selectionText,
    selectionLen: params.selectionText?.length,
    paragraphIndex: params.paragraphIndex,
    changeTrackingMode: params.changeTrackingMode,
    domSummary: params.domSummary,
  };
}

/** @deprecated Verbose logging removed; kept as no-ops for call sites. */
export function logWordEditCapture(_stage: string, _detail: Record<string, unknown>): void {}

/** @deprecated Verbose logging removed; kept as no-ops for call sites. */
export function logWordEditCaptureWarn(_stage: string, _detail: Record<string, unknown>): void {}

/** @deprecated Verbose logging removed; kept as no-ops for call sites. */
export function logAcceptedTextDelta(
  _label: string,
  _before: string,
  _after: string,
  _extra?: Record<string, unknown>,
): void {}

/** @deprecated Verbose logging removed; kept as no-ops for call sites. */
export function logParagraphSnapshot(_stage: string, _snapshot: ParagraphEditSnapshot): void {}

/** Summarize contentEditable track-changes HTML (clause-card edit diagnostics). */
export function summarizeEditableDom(root: HTMLElement): Record<string, unknown> {
  const insEls = root.querySelectorAll('ins');
  const delEls = root.querySelectorAll('del');
  const segments: Array<Record<string, unknown>> = [];

  const walk = (node: Node, insideDel: boolean) => {
    if (node.nodeType === Node.TEXT_NODE) {
      if (!insideDel) {
        const t = node.textContent || '';
        if (t) segments.push({ kind: 'text', len: t.length, preview: t.slice(0, 60) });
      }
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as Element;
    const tag = el.tagName.toLowerCase();
    if (tag === 'del' || el.classList.contains('track-change-deletion')) {
      segments.push({
        kind: 'del',
        author: el.getAttribute('title') || null,
        len: (el.textContent || '').length,
        preview: (el.textContent || '').slice(0, 60),
      });
      return;
    }
    if (tag === 'ins' || el.classList.contains('track-change-insertion')) {
      segments.push({
        kind: 'ins',
        author: el.getAttribute('title') || null,
        isBlue: el.classList.contains('track-change-new'),
        len: (el.textContent || '').length,
        preview: (el.textContent || '').slice(0, 60),
      });
      for (const child of Array.from(el.childNodes)) walk(child, false);
      return;
    }
    for (const child of Array.from(el.childNodes)) walk(child, insideDel);
  };

  for (const child of Array.from(root.childNodes)) walk(child, false);

  return {
    insCount: insEls.length,
    delCount: delEls.length,
    segmentCount: segments.length,
    segments: segments.slice(0, 40),
    innerTextLen: root.innerText?.length ?? 0,
    textContentLen: root.textContent?.length ?? 0,
  };
}

/** Opt-in via localStorage `courtyard:enableWordEditCapture=1` (disabled by default). */
export function isWordEditCaptureEnabled(): boolean {
  try {
    return localStorage.getItem('courtyard:enableWordEditCapture') === '1';
  } catch {
    return false;
  }
}
